import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useUser, useSignIn, useSignUp, useAuth as useClerkAuth } from '@clerk/react';
import { ApiError, setTokenGetter, setAuthTokenOverride, api } from '../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  role: string;
  name: string | null;
  status: 'PENDING' | 'ACTIVE';
  onboardingCompleted: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  authError: string | null;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, name?: string) => Promise<{ error: string | null }>;
  signOut: () => void;
  refreshUser: () => Promise<void>;
}

interface PlatformUser {
  id: string;
  email: string;
  role: string;
  name: string | null;
  status: 'PENDING' | 'ACTIVE';
  onboardingCompleted: boolean;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, user: clerkUser } = useUser();
  const { signIn: clerkSignIn } = useSignIn();
  const { signUp: clerkSignUp } = useSignUp();
  const { getToken, signOut: clerkSignOut } = useClerkAuth();

  const [platformUser, setPlatformUser] = useState<PlatformUser | null>(null);
  const [platformUserLoaded, setPlatformUserLoaded] = useState(false);
  const [platformUserError, setPlatformUserError] = useState<string | null>(null);

  useEffect(() => {
    setTokenGetter(() => getToken());
  }, [getToken]);

  const fetchPlatformUser = async () => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const token = await getToken();
        if (!token) {
          throw new Error('Clerk token not ready');
        }

        setAuthTokenOverride(token);
        const data = await api.get<PlatformUser>('/auth/me');
        setPlatformUser(data);
        setPlatformUserError(null);
        setPlatformUserLoaded(true);
        return;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Failed to load account');
        setAuthTokenOverride(null);
        await sleep(300 * (attempt + 1));
      }
    }

    try {
      throw lastError ?? new Error('Failed to load account');
    } catch (error) {
      const resolvedError =
        error instanceof Error ? error : new Error('Failed to load account');
      console.error('Failed to fetch platform user', resolvedError);
      setPlatformUser(null);
      setPlatformUserError(resolvedError.message);

      if (error instanceof ApiError && error.status === 401) {
        setAuthTokenOverride(null);
        await clerkSignOut({ redirectUrl: '/login' });
      }
    } finally {
      setPlatformUserLoaded(true);
    }
  };

  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn) {
      setPlatformUser(null);
      setPlatformUserLoaded(false);
      setPlatformUserError(null);
      fetchPlatformUser();
    } else {
      setPlatformUser(null);
      setPlatformUserLoaded(false);
      setPlatformUserError(null);
      setAuthTokenOverride(null);
    }
  }, [isLoaded, isSignedIn]);

  const loading = !isLoaded || (!!isSignedIn && !platformUserLoaded);

  const user: AuthUser | null =
    isSignedIn && clerkUser && platformUser
      ? {
          id: platformUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress ?? '',
          role: platformUser.role,
          name: clerkUser.fullName,
          status: platformUser.status,
          onboardingCompleted: platformUser.onboardingCompleted,
        }
      : null;

  const signIn = async (email: string, password: string): Promise<{ error: string | null }> => {
    if (!clerkSignIn) return { error: 'Auth not ready' };
    try {
      const signInResource = clerkSignIn as any;
      const result = await signInResource.create({ identifier: email, password });
      if (result.status === 'complete') {
        await (signInResource.setActive?.({ session: result.createdSessionId }) ?? Promise.resolve());
        return { error: null };
      }
      return { error: 'Verificación adicional requerida' };
    } catch (err: unknown) {
      const clerkErr = err as { errors?: Array<{ longMessage?: string; message?: string }> };
      return {
        error: clerkErr?.errors?.[0]?.longMessage ?? clerkErr?.errors?.[0]?.message ?? 'Sign in failed',
      };
    }
  };

  const signUp = async (
    email: string,
    password: string,
    name?: string,
  ): Promise<{ error: string | null }> => {
    if (!clerkSignUp) return { error: 'Auth not ready' };
    try {
      const parts = name?.trim().split(' ') ?? [];
      const firstName = parts[0] || undefined;
      const lastName = parts.slice(1).join(' ') || undefined;
      const signUpResource = clerkSignUp as any;
      const result = await signUpResource.create({ emailAddress: email, password, firstName, lastName });
      if (result.status === 'complete') {
        await (signUpResource.setActive?.({ session: result.createdSessionId }) ?? Promise.resolve());
        return { error: null };
      }
      return { error: 'Verificación adicional requerida. Revisa tu email.' };
    } catch (err: unknown) {
      const clerkErr = err as { errors?: Array<{ longMessage?: string; message?: string }> };
      return {
        error: clerkErr?.errors?.[0]?.longMessage ?? clerkErr?.errors?.[0]?.message ?? 'Sign up failed',
      };
    }
  };

  const signOut = () => {
    setAuthTokenOverride(null);
    clerkSignOut({ redirectUrl: '/login' });
  };

  const refreshUser = async () => {
    setPlatformUser(null);
    setPlatformUserLoaded(false);
    await fetchPlatformUser();
  };

  return (
    <AuthContext.Provider
      value={{ user, token: null, loading, authError: platformUserError, signIn, signUp, signOut, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
